import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { snackbar } from '@occc-app/design-system';

import { RetailerNetworkUpdatePage } from './retailer-network-update-page.component';

import { useGetRetailerNetworkQuery } from '../../data-access/queries';
import { useRetailerNetworkUpdateMutation } from '../../data-access/mutations';
import { RetailerNetworkConfigurationFormValues } from '../retailer-network-configuration/retailer-network-configuration.types';
import { getRetailerNetworkConfigurationUpdateInput } from '../retailer-network-configuration/retailer-network-configuration.utils';
import {
  RetailerNetworkConfigurationTabs,
  RetailerNetworkUpdateHandler,
} from '../retailer-connect.types';
import { getTabValue, routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';

export const RetailerNetworkUpdatePageContainer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { networkId = '' } = useParams<{ networkId: string }>();

  const { data, loading, error } = useGetRetailerNetworkQuery({
    variables: { uuid: networkId },
  });
  const retailerNetwork = data?.retailerNetwork;

  const tabValue = useMemo(() => getTabValue(pathname), [pathname]);

  const setTabValue = (value: RetailerNetworkConfigurationTabs) => {
    navigate(value);
  };

  const [retailerNetworkUpdateMutation] = useRetailerNetworkUpdateMutation({
    onCompleted: () => {
      snackbar.success('Retailer Network has been successfully updated');
    },
    refetchQueries: ['retailerNetwork'],
  });

  const retailerNetworkUpdate: RetailerNetworkUpdateHandler = async (input, refetchQueries) => {
    if (retailerNetwork) {
      await retailerNetworkUpdateMutation({
        refetchQueries,
        variables: {
          input: {
            uuid: retailerNetwork.uuid,
            ...input,
          },
        },
      });
    }
  };

  const retailerNetworkConfigurationUpdate = async (
    values: RetailerNetworkConfigurationFormValues,
  ) => {
    const input = getRetailerNetworkConfigurationUpdateInput(values);
    await retailerNetworkUpdate(input);
    if (retailerNetwork?.uuid) {
      navigate(routeCreator.overview(retailerNetwork?.uuid));
    }
  };

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (loading && !retailerNetwork) {
    return (
      <Box p={3} display='flex' justifyContent='center' alignItems='center'>
        <CircularProgress />
      </Box>
    );
  }

  if (!retailerNetwork) {
    return <RetailerNetworkNoData />;
  }

  return (
    <RetailerNetworkUpdatePage
      tabValue={tabValue}
      setTabValue={setTabValue}
      retailerNetworkUpdate={retailerNetworkUpdate}
      retailerNetwork={retailerNetwork}
      retailerNetworkConfigurationUpdate={retailerNetworkConfigurationUpdate}
    />
  );
};
